import React from "react";
import { canonical } from "../data/business";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, Wrench, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import BeforeAfter from "../components/BeforeAfter";
import QuoteForm from "../components/QuoteForm";
import { projects, findProject } from "../data/projects";
import { findService } from "../data/services";
import { findLocation } from "../data/locations";

export default function ProjectDetail() {
  const { slug } = useParams();
  const p = findProject(slug);
  if (!p) return <Navigate to="/projects" replace />;

  const service = findService(p.service);
  const location = findLocation(p.location);

  const jsonLd = { "@context": "https://schema.org", "@type": "CreativeWork", "name": p.title, "image": p.gallery, "about": service?.name, "locationCreated": location?.name };

  const related = projects.filter((x) => x.slug !== p.slug).slice(0, 3);

  return (
    <div className="page-enter">
      <Seo title={`${p.title} | Last Cut Landscaping Project`} description={p.summary} canonical={canonical(`/projects/${p.slug}`)} jsonLd={jsonLd} />

      <section className="bg-cream pt-8 lg:pt-12 pb-12">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Breadcrumbs items={[{ label: "Projects", to: "/projects" }, { label: p.title }]} />
          <div className="grid lg:grid-cols-12 gap-10 mt-6 items-end">
            <div className="lg:col-span-7">
              <div className="flex gap-2 mb-4">
                {service && <Link to={`/services/${service.slug}`} className="pill"><Wrench size={12}/> {service.name}</Link>}
                {location && <Link to={`/locations/${location.slug}`} className="pill"><MapPin size={12}/> {location.name}</Link>}
              </div>
              <h1 className="text-display font-display text-[clamp(2.2rem,5vw,4rem)] text-dark">{p.title}</h1>
              <p className="text-lg text-dark/75 mt-5">{p.summary}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden aspect-[4/3]"><img src={p.after} alt={p.title} className="w-full h-full object-cover"/></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <BeforeAfter before={p.before} after={p.after} beforeAlt={`${p.title} before`} afterAlt={`${p.title} after`}/>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-paper">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Project details</div>
            <h2 className="font-display text-3xl lg:text-4xl text-dark">The story behind this project</h2>
            <p className="text-dark/75 text-[16px] leading-relaxed mt-4">{p.details}</p>
            {service && (
              <ul className="grid sm:grid-cols-2 gap-3 mt-6">
                {service.benefits.slice(0,4).map((b,i)=><li key={i} className="flex gap-2 p-3 bg-white rounded-xl text-sm text-dark/80"><Check size={16} className="text-[color:var(--lc-green)] mt-0.5"/>{b}</li>)}
              </ul>
            )}
          </div>
          <div className="lg:col-span-5">
            <QuoteForm defaultService={service?.name || ""} sourcePage={`/projects/${p.slug}`} />
          </div>
        </div>
      </section>

      {p.gallery && p.gallery.length > 1 && (
        <section className="py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-display text-3xl lg:text-4xl text-dark mb-8">Gallery</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {p.gallery.map((g,i)=><div key={i} className="rounded-2xl overflow-hidden aspect-[4/3]"><img src={g} alt={`${p.title} photo ${i+1}`} loading="lazy" className="w-full h-full object-cover"/></div>)}
            </div>
          </div>
        </section>
      )}

      <section className="py-14 lg:py-20 bg-paper">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="font-display text-3xl lg:text-4xl text-dark mb-8">Related projects</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r)=>(
              <Link key={r.slug} to={`/projects/${r.slug}`} className="lc-card group">
                <div className="aspect-[4/3] overflow-hidden"><img src={r.after} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/></div>
                <div className="p-5"><h3 className="font-display text-lg text-dark">{r.title}</h3><p className="text-sm text-dark/65 mt-1.5 line-clamp-2">{r.summary}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
